import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Switch,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { automationAPI } from '../../services/api';
import AutomationRuleDialog from '../../components/Automation/AutomationRuleDialog';

const AutomationRulesPage: React.FC = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await automationAPI.getAutomationRules();
      setRules(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError('Failed to load automation rules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (id: number, currentEnabled: boolean) => {
    try {
      await automationAPI.toggleAutomationRule(id, !currentEnabled);
      loadRules();
    } catch (err) {
      setError('Failed to toggle automation rule');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this automation rule?')) {
      return;
    }
    try {
      await automationAPI.deleteAutomationRule(id);
      loadRules();
    } catch (err) {
      setError('Failed to delete automation rule');
    }
  };

  const handleClone = async (id: number) => {
    try {
      await automationAPI.cloneAutomationRule(id);
      loadRules();
    } catch (err) {
      setError('Failed to clone automation rule');
    }
  };

  const getEventLabel = (eventName: string) => {
    const labels: Record<string, string> = {
      'conversation_created': 'Conversation Created',
      'conversation_updated': 'Conversation Updated',
      'message_created': 'Message Created',
      'conversation_opened': 'Conversation Opened'
    };
    return labels[eventName] || eventName;
  };

  const handleCreate = () => {
    setEditingRuleId(null);
    setDialogOpen(true);
  };

  const handleEdit = (id: number) => {
    setEditingRuleId(id);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingRuleId(null);
  };

  const handleDialogSave = () => {
    loadRules();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Automation Rules</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create Rule
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Conditions</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading && rules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No automation rules found. Create one to get started!
                  </TableCell>
                </TableRow>
              )}
              {!loading && rules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      {rule.name}
                    </Typography>
                    {rule.description && (
                      <Typography variant="body2" color="text.secondary">
                        {rule.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={getEventLabel(rule.trigger_event)} size="small" />
                  </TableCell>
                  <TableCell>{rule.conditions_count || 0}</TableCell>
                  <TableCell>{rule.actions_count || 0}</TableCell>
                  <TableCell>
                    <Switch
                      checked={rule.enabled}
                      onChange={() => handleToggleEnabled(rule.id, rule.enabled)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(rule.id)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleClone(rule.id)}
                      title="Clone"
                    >
                      <CopyIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(rule.id)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AutomationRuleDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        ruleId={editingRuleId}
      />
    </Box>
  );
};

export default AutomationRulesPage;
